<template>
  <HeaderBar 
    :currentDay="1" 
    @showLogin="showLoginDialog = true" 
    @showAddTask="showAddTaskDialog = true"
    @refresh="fetchUserTaskStats" 
  />
  <div class="user-list-container">
    <h2>学生任务完成情况</h2>
    <div style="margin-bottom: 20px; display: flex; align-items: center; gap: 10px;">
      <el-input v-model="username" placeholder="请输入姓名" clearable :style="{ width: isMobile ? '100%' : '200px' }" />
      <el-button type="primary" @click="onSearch">检索</el-button>
    </div>

    <div v-if="loading" class="loading-indicator">
      <el-spinner /> 正在加载数据...
    </div>

    <div v-else-if="error" class="error-message">
      <el-alert type="error" :title="error" show-icon />
    </div>

    <el-table
      v-else
      :data="tableData"
      border
      stripe
      style="width: 100%"
      class="user-tasks-table"
    >
      <el-table-column prop="username" label="姓名" fixed="left" width="120">
        <template #default="scope">
          <span 
            class="clickable-username"
            @click="handleUserClick(scope.row.userId)"
          >
            {{ scope.row.username }}
          </span>
        </template>
      </el-table-column>
      
      <el-table-column
        v-for="day in dayColumns"
        :key="day.prop"
        :prop="day.prop"
        :label="day.label"
        width="100"
        align="center"
      >
        <template #default="scope">
          <el-tag 
            :type="scope.row[day.prop] > 0 ? 'danger' : 'success'"
            size="small"
          >
            {{ scope.row[day.prop] }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="score" label="点名"  width="100" align="center">
        <template #default="scope">
          <el-tag 
            :type="scope.row.score > 0 ? 'danger' : 'success'"
            size="large"
          >
            {{ scope.row.score }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="count" label="总计" fixed="right" width="100" align="center">
        <template #default="scope">
          <el-tag 
            :type="scope.row.count > 0 ? 'danger' : 'success'"
            size="large"
          >
            {{ scope.row.count }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="!loading && !error && tableData.length === 0" description="暂无学生任务数据" />
  </div>

  <!-- 添加用户信息侧边栏组件 -->
  <user-profile-drawer
    v-model="showUserProfile"
    :user-id="selectedUserId"
    @show-login="showLoginDialog = true"
  />

  <!-- 登录弹窗 -->
  <el-dialog title="登录" v-model="showLoginDialog" width="30%" :fullscreen="isMobile">
    <LoginDialog @close="handleLoginSuccess" />
  </el-dialog>

  <!-- 添加任务弹窗 -->
  <el-dialog title="添加任务" v-model="showAddTaskDialog" width="50%" :fullscreen="isMobile">
    <AddTaskDialog @close="handleAddTaskSuccess" />
  </el-dialog>
</template>

<script>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import taskApi from '../api/task';
import { ElMessage } from 'element-plus';
import HeaderBar from '../components/HeaderBar.vue';
import UserProfileDrawer from '../components/UserProfileDrawer.vue';
import LoginDialog from '../components/LoginDialog.vue';
import AddTaskDialog from '../components/AddTaskDialog.vue';

export default {
  name: 'UserList',
  components: {
    HeaderBar,
    UserProfileDrawer,
    LoginDialog,
    AddTaskDialog,
  },
  setup() {
    const tableData = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const showUserProfile = ref(false);
    const selectedUserId = ref(null);
    const showLoginDialog = ref(false);
    const showAddTaskDialog = ref(false);
    const isMobile = ref(false);
    const username = ref('');

    const dayColumns = computed(() => {
      const columns = [];
      if (tableData.value.length > 0) {
        const firstRow = tableData.value[0];
        const dayKeys = Object.keys(firstRow).filter(key => key.startsWith('day'));
        dayKeys.sort((a, b) => parseInt(a.substring(3)) - parseInt(b.substring(3))); // 确保 Day 列按数字顺序排列
        dayKeys.forEach(key => {
          columns.push({
            prop: key,
            label: key.replace('day', 'Day'),
          });
        });
      }
      return columns;
    });

    const fetchUserTaskStats = async (searchName = '') => {
      loading.value = true;
      error.value = null;
      try {
        const response = await taskApi.getAllUserTaskStats(searchName); 
        if (response.code === '000000') {
          tableData.value = response.data;
        } else {
          throw new Error(response.msg || '获取数据失败');
        }
      } catch (err) {
        error.value = '加载学生任务数据失败: ' + err.message;
        ElMessage.error(error.value);
      } finally {
        loading.value = false;
      }
    };

    const onSearch = () => {
      fetchUserTaskStats(username.value);
    };

    const handleUserClick = (userId) => {
      if (!userId) {
        ElMessage.warning('用户ID无效');
        return;
      }
      selectedUserId.value = userId;
      showUserProfile.value = true;
    };

    const handleLoginSuccess = () => {
      showLoginDialog.value = false;
      // 登录成功后可以刷新数据
      fetchUserTaskStats();
    };

    const handleAddTaskSuccess = () => {
      showAddTaskDialog.value = false;
      // 添加任务成功后可以刷新数据
      fetchUserTaskStats();
    };

    // 检查是否为移动端
    const checkIsMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    onMounted(() => {
      checkIsMobile();
      window.addEventListener('resize', checkIsMobile);
      fetchUserTaskStats();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkIsMobile);
    });

    return {
      tableData,
      loading,
      error,
      dayColumns,
      showUserProfile,
      selectedUserId,
      showLoginDialog,
      showAddTaskDialog,
      isMobile,
      handleUserClick,
      handleLoginSuccess,
      handleAddTaskSuccess,
      username,
      onSearch,
    };
  },
};
</script>

<style scoped>
.user-list-container {
  padding: 20px;
  max-width: 1200px;
  margin: 20px auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  color: var(--el-text-color-primary);
  margin-bottom: 30px;
}

.loading-indicator,
.error-message {
  text-align: center;
  padding: 20px;
}

.user-tasks-table {
  margin-top: 20px;
}

.clickable-username {
  cursor: pointer;
  color: var(--el-color-primary);
  text-decoration: underline;
}

.clickable-username:hover {
  opacity: 0.8;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .user-list-container {
    padding: 10px;
    margin: 10px auto;
    width: 98%;
  }

  h2 {
    font-size: 1.2em;
    margin-bottom: 20px;
  }

  .user-tasks-table {
    /* 使表格可以水平滚动 */
    overflow-x: auto;
  }
}

</style> 